import React, { useEffect } from 'react';
import { useState } from 'react';
import { flowRight } from 'lodash/fp';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import ReactSelect from 'react-select';
import * as api from '../../../../api';

import AutosuggestText from 'ui/AutosuggestText';
import CancelIcon from 'ui/CancelIcon';
import { isValidEmail } from 'utils/validators';
import RecipientsList from './RecipientsList';
import { withAppContext } from '../../../AppContext';

import {
    Table,
    TableRow,
    TableCell,
    TableHead,
    Select,
    OutlinedInput,
} from '@material-ui/core';
import { RecipientsPermission } from '../constants';

const styles = theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        paddingTop: '20px',
    },

    info: {
        flex: 1,
    },

    noBorder: {
        borderBottom: 0,
    },
    autoSuggest: {
        marginBottom: 0,
    },
});

const AddRecipients = props => {
    const {
        app,
        newRecipientsInfo,
        currentVault,
        onAddRecipient,
        onChangePermission,
        onRemoveRecipient,
        classes,
    } = props;

    const [newRecipientInfo, setNewRecipientInfo] = useState({
        permissions: 'editor',
    });
    const [subCompanyInfo, setSubCompanyInfo] = useState({});
    const [availableRecipients, setAvailableRecipients] = useState([]);

    const [error, setError] = useState(false);

    useEffect(() => {
        let organizationUuid;

        if (!subCompanyInfo.name) {
            // No sub-company selected - use current user's organization
            if (!app?.currentUser?.organizations?.[0]?.uuid) return;
            organizationUuid = app.currentUser.organizations[0].uuid;
        } else {
            const company = currentVault.available_sub_companies.find(
                company => company.name === subCompanyInfo.name
            );
            if (!company) return;
            organizationUuid = company.uuid;
        }

        const fetchAvailableRecipients = async () => {
            const response = await api.get(
                `/api/organization/${organizationUuid}/vault_recipients?vault_uuid=${currentVault.uuid}`
            );
            setAvailableRecipients(response.data.available_recipients || []);
        };
        void fetchAvailableRecipients();
    }, [subCompanyInfo, app?.currentUser, currentVault.uuid]);

    const renderInputComponent = inputProps => {
        const { classes, inputRef = () => {}, ref, ...other } = inputProps;

        return (
            <TextField
                required
                label="Enter group or recipient name"
                className={classes.autoSuggest}
                margin="normal"
                variant="outlined"
                fullWidth
                error={error}
                helperText={error && "Group doesn't exist"}
                InputProps={{
                    inputRef: node => {
                        ref(node);
                        inputRef(node);
                    },
                }}
                {...other}
            />
        );
    };

    const selectSubCompany = option => {
        setSubCompanyInfo({
            name: option ? option.label : '',
        });
        if (!option) {
            setError(false);
        }
    };

    const cleanUpForm = () => {
        setNewRecipientInfo({
            name: '',
            permissions: 'editor',
        });
        setError(false);
    };

    const addNewRecipient = () => {
        const selected = availableRecipients.find(
            i => i.display_name === newRecipientInfo.name
        );
        if (selected) {
            onAddRecipient({
                display_name: selected.display_name,
                name: selected.name,
                type: selected.type,
                permissions: newRecipientInfo.permissions,
                uuid: selected.uuid,
            });
            cleanUpForm();
        } else if (isValidEmail(newRecipientInfo.name)) {
            onAddRecipient({
                display_name: newRecipientInfo.name,
                name: newRecipientInfo.name,
                permissions: newRecipientInfo.permissions,
                type: 'invitation',
            });
            cleanUpForm();
        } else {
            setError(true);
        }
    };

    const selectRecipient = val => {
        setNewRecipientInfo({
            ...newRecipientInfo,
            name: val,
        });
        if (!val) {
            setError(false);
        }
    };

    const selectPermission = (prop, val) => {
        setNewRecipientInfo({
            ...newRecipientInfo,
            permissions: val.props.value,
        });
    };

    const existing = new Set(newRecipientsInfo.map(r => r.name));
    const filteredRecipients = availableRecipients.filter(
        r => !existing.has(r.name)
    );

    const currentUserOrgUuid = app?.currentUser?.organizations?.[0]?.uuid;
    const filteredSubCompanies =
        currentVault.available_sub_companies?.filter(
            company => company.uuid !== currentUserOrgUuid
        ) || [];

    return (
        <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell
                            style={{
                                width: '100%',
                                fontWeight: 'bold',
                            }}
                        >
                            Select recipient company to load
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <div style={{ flex: 1 }}>
                                    <ReactSelect
                                        value={
                                            subCompanyInfo.name
                                                ? {
                                                      label:
                                                          subCompanyInfo.name,
                                                      value:
                                                          subCompanyInfo.name,
                                                  }
                                                : null
                                        }
                                        onChange={selectSubCompany}
                                        options={filteredSubCompanies.map(
                                            item => ({
                                                label: item.name,
                                                value: item.uuid,
                                            })
                                        )}
                                        isSearchable
                                        placeholder="Select sub company"
                                        noOptionsMessage={() =>
                                            'No companies found'
                                        }
                                        styles={{
                                            menuPortal: base => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                        }}
                                        menuPortalTarget={document.body}
                                    />
                                </div>
                                {subCompanyInfo.name && (
                                    <IconButton
                                        onClick={() => selectSubCompany(null)}
                                        style={{ padding: 8 }}
                                        title="Clear sub company selection"
                                    >
                                        <CancelIcon />
                                    </IconButton>
                                )}
                            </div>
                        </TableCell>
                    </TableRow>
                </TableHead>
            </Table>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell
                            style={{
                                width: '531px',
                                fontWeight: 'bold',
                            }}
                        >
                            Recipient
                        </TableCell>
                        <TableCell
                            style={{
                                minWidth: '200px',
                                fontWeight: 'bold',
                            }}
                        >
                            Permission
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.noBorder}>
                            <AutosuggestText
                                renderInputComponent={renderInputComponent}
                                value={newRecipientInfo.name}
                                suggestions={filteredRecipients.map(item => ({
                                    label: item.display_name,
                                }))}
                                onChange={selectRecipient}
                            />
                        </TableCell>
                        <TableCell className={classes.noBorder}>
                            <Select
                                style={{
                                    minWidth: '241px',
                                }}
                                value={newRecipientInfo.permissions}
                                input={
                                    <OutlinedInput
                                        labelWidth={0}
                                        name="permissions"
                                        id="outlined-age-native-simple"
                                    />
                                }
                                onChange={selectPermission}
                            >
                                {RecipientsPermission.map(
                                    (permission, index) => (
                                        <MenuItem
                                            key={index}
                                            value={permission.key}
                                        >
                                            {permission.name}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <Typography className={classes.info}>
                                After adding users click Save at the bottom
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Button
                                style={{ float: 'right' }}
                                color="secondary"
                                variant="contained"
                                disabled={!newRecipientInfo.name}
                                onClick={addNewRecipient}
                            >
                                Add
                            </Button>
                        </TableCell>
                    </TableRow>
                </TableHead>
            </Table>

            {newRecipientsInfo.length > 0 && (
                <div className={classes.container}>
                    <RecipientsList
                        recipients={newRecipientsInfo}
                        permissionsOptions={RecipientsPermission}
                        onChange={onChangePermission}
                        onRemove={onRemoveRecipient}
                        maxHeight={240}
                    />
                </div>
            )}
        </>
    );
};

export default flowRight(withAppContext, withStyles(styles))(AddRecipients);
